import { useState } from "react";
import { Check, Trash2, Loader2 } from "lucide-react";
import { formatMoney } from "@/lib/currency";

const CATEGORIES = [
  "Food", "Groceries", "Dining", "Coffee",
  "Transport", "Travel",
  "Utilities", "Rent",
  "Shopping",
  "Entertainment",
  "Health", "Healthcare",
  "Education",
  "Insurance",
  "Software",
  "Other",
];

/**
 * InboxRow — a single Plaid-imported expense row. Lets the user pick a
 * category, approve (with optional override), or delete. Extracted from
 * TransactionsInbox.jsx in iter-47.
 */
export default function InboxRow({ row, currency, busy, onApprove, onDelete, t }) {
  const [category, setCategory] = useState(row.category || "Other");
  // Ensure the row's original category is an option even if not in our list
  const options = CATEGORIES.includes(row.category)
    ? CATEGORIES
    : [row.category || "Other", ...CATEGORIES];

  const dateShort = (() => {
    try {
      return new Date(row.date).toLocaleDateString(undefined, {
        month: "short", day: "numeric",
      });
    } catch { return row.date; }
  })();

  return (
    <li
      className="py-3 flex items-center gap-3 flex-wrap"
      data-testid={`inbox-row-${row.id}`}
    >
      <div className="flex-1 min-w-[180px]">
        <div className="font-medium text-sm text-[var(--text-primary)] truncate">
          {row.description || t("inbox.row_bank_transaction")}
        </div>
        <div className="text-[11px] text-[var(--text-muted)] mt-0.5">
          {dateShort} · <span className="font-mono">{row.plaid_currency || currency}</span>
        </div>
      </div>

      <div className="num-md text-[var(--text-primary)] w-24 text-right shrink-0">
        {formatMoney(row.amount, currency)}
      </div>

      <select
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        className="field-input text-xs !py-1.5 !px-2 !w-32 shrink-0"
        aria-label={t("inbox.row_category_label")}
        data-testid={`inbox-category-${row.id}`}
      >
        {options.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>

      <div className="flex items-center gap-1.5 shrink-0">
        <button
          className="btn-primary !py-1.5 !px-2.5 text-xs flex items-center gap-1"
          onClick={() => onApprove(row, category)}
          disabled={busy}
          data-testid={`inbox-approve-${row.id}`}
          title={t("inbox.row_approve_title")}
        >
          {busy ? <Loader2 size={13} className="animate-spin"/> : <Check size={13}/>}
          {t("inbox.row_approve")}
        </button>
        <button
          className="btn-ghost !py-1.5 !px-2 text-xs"
          onClick={() => onDelete(row)}
          disabled={busy}
          data-testid={`inbox-delete-${row.id}`}
          title={t("inbox.row_delete_title")}
          style={{ color: "var(--text-muted)" }}
        >
          <Trash2 size={13}/>
        </button>
      </div>
    </li>
  );
}
